// pageVideoBlockUtils.js

export const PORTAL_AUDIENCE = Object.freeze({
  HOSTED: 'hosted',
  INTERNAL: 'internal',
});

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * Returns whether page playback should use hosted/public routes (PIN, public manifest API).
 *
 * @param {object|null} [pagePlaybackContext]
 * @returns {boolean}
 */
export function isHostedPageAudience(pagePlaybackContext = null) {
  if (!pagePlaybackContext || typeof pagePlaybackContext !== 'object') {
    return false;
  }

  if (pagePlaybackContext.audience === PORTAL_AUDIENCE.INTERNAL) {
    return false;
  }

  if (pagePlaybackContext.audience === PORTAL_AUDIENCE.HOSTED) {
    return true;
  }

  return pagePlaybackContext.publicPlayback === true;
}

/**
 * Returns whether page playback is for base members inside the app.
 *
 * @param {object|null} [pagePlaybackContext]
 * @returns {boolean}
 */
export function isInternalPageAudience(pagePlaybackContext = null) {
  return !isHostedPageAudience(pagePlaybackContext);
}

/**
 * Logs page video playback diagnostics. Filter the browser console by `[page-video]`.
 *
 * @param {string} label - Short event label.
 * @param {object} [data] - Structured debug payload.
 */
export function logPageVideoDebug(label, data = {}) {
  if (typeof console === 'undefined' || typeof console.info !== 'function') {
    return;
  }

  console.info('[page-video]', label, data);
}

function getVideoNodeMediaRefs(node) {
  return {
    storagePath: normalizeString(node?.storagePath || node?.data_video?.storagePath),
    url: normalizeString(node?.url || node?.data_video?.url),
  };
}

function isHlsManifestPath(value) {
  const normalized = normalizeString(value);
  return normalized.includes('.m3u8') || normalized.includes('/secureHlsManifest?ticket=');
}

/**
 * Returns whether a video node has any uploaded media metadata available.
 *
 * @param {object|null} node - Video node row.
 * @returns {boolean}
 */
export function hasPageVideoMedia(node) {
  if (!node || normalizeString(node?.type).toUpperCase() !== 'VIDEO') {
    return false;
  }

  const { storagePath, url } = getVideoNodeMediaRefs(node);
  return Boolean(url || storagePath);
}

/**
 * Returns whether a video node has an HLS manifest ready for streaming playback.
 *
 * @param {object|null} node - Video node row.
 * @returns {boolean}
 */
export function isPageVideoHlsReady(node) {
  if (!hasPageVideoMedia(node)) {
    return false;
  }

  const { storagePath, url } = getVideoNodeMediaRefs(node);
  return isHlsManifestPath(url) || isHlsManifestPath(storagePath);
}

/**
 * Returns whether a page VIDEO block should render the player for the current surface.
 *
 * Authenticated surfaces can play uploaded originals while transcoding finishes.
 * Public page playback requires HLS.
 *
 * @param {object|null} node - Video node row.
 * @param {object|null} [pagePlaybackContext] - Public page playback context.
 * @returns {boolean}
 */
export function isPageVideoPlayable(node, pagePlaybackContext = null) {
  if (!hasPageVideoMedia(node)) {
    return false;
  }

  if (isHostedPageAudience(pagePlaybackContext)) {
    return isPageVideoHlsReady(node);
  }

  return true;
}

/**
 * Returns whether a video node is still processing after upload.
 *
 * @param {object|null} node - Video node row.
 * @param {object|null} [pagePlaybackContext] - Page playback context.
 * @returns {boolean}
 */
export function isPageVideoProcessing(node, pagePlaybackContext = null) {
  if (!node || normalizeString(node?.type).toUpperCase() !== 'VIDEO') {
    return false;
  }

  if (!hasPageVideoMedia(node)) {
    return true;
  }

  if (isHostedPageAudience(pagePlaybackContext)) {
    return !isPageVideoHlsReady(node);
  }

  return false;
}

/**
 * Collects node keys referenced by NODE and VIDEO blocks, including content zone interiors.
 *
 * @param {object} blocksByKey - Layout blocks keyed by block key.
 * @returns {string[]}
 */
export function collectReferencedVideoNodeKeysFromBlocks(blocksByKey) {
  const keys = new Set();

  const visitBlock = (block) => {
    if (!block || typeof block !== 'object') {
      return;
    }

    const blockType = normalizeString(block?.type).toUpperCase();

    if ((blockType === 'NODE' || blockType === 'VIDEO') && normalizeString(block?.nodeKey)) {
      keys.add(normalizeString(block.nodeKey));
    }

    if (blockType === 'CONTENT_ZONE' && block?.zone?.blocksByKey) {
      Object.values(block.zone.blocksByKey).forEach(visitBlock);
    }
  };

  Object.values(blocksByKey || {}).forEach(visitBlock);

  return Array.from(keys);
}

/**
 * Returns whether merged page blocks include at least one VIDEO block with a node key.
 *
 * @param {object} blocksByKey - Merged layout blocks keyed by block key.
 * @returns {boolean}
 */
export function layoutHasVideoBlocks(blocksByKey) {
  return collectReferencedVideoNodeKeysFromBlocks(blocksByKey).length > 0;
}

/**
 * Returns video node keys referenced by a public page payload that still await HLS.
 *
 * @param {object|null} payload - Public page payload.
 * @returns {string[]}
 */
export function getPublicPageVideosAwaitingHls(payload) {
  const videoNodeKeys = collectReferencedVideoNodeKeysFromBlocks(payload?.page?.blocksByKey);
  const nodesByKey = payload?.dependencies?.nodesByKey || {};

  return videoNodeKeys.filter((nodeKey) => {
    const node = nodesByKey[nodeKey];
    return hasPageVideoMedia(node) && !isPageVideoHlsReady(node);
  });
}

/**
 * Returns whether a public unpinned page should show streaming cost warnings.
 *
 * @param {object|null} pageDoc - Page instance document.
 * @param {object} blocksByKey - Merged layout blocks keyed by block key.
 * @returns {boolean}
 */
export function shouldShowPageVideoStreamingWarning(pageDoc, blocksByKey) {
  if (!layoutHasVideoBlocks(blocksByKey)) {
    return false;
  }

  const accessMode = normalizeString(pageDoc?.accessMode).toUpperCase();
  const hasPin = Boolean(normalizeString(pageDoc?.pinHash));

  return accessMode === 'PUBLIC' && !hasPin;
}
